import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINT, APP_ID } from "../../utils/constants";
import useTemplateSettings from "../../hooks/useTemplateSettings";
import MuPb from "../../widgets/MuPb";
import {
  getDbHeader,
  getErrorResponse,
  saveReg,
  toastError,
} from "../../utils/util";
import BannerSlider from "../templates/slider/BannerSlider";
import RegFooter from "../templates/RegFooter";
import CountryStateCity from "../widgets/CountryStateCity";
import MyDatePicker from "../datetime/MyDatePicker";
import SelectDropDown from "../SelectDropdown";
import ParagraphDescription from "../ParagraphDescription";
import MyTimePicker from "../datetime/MyTimePicker";
import MyDateRange from "../datetime/MyDateRange";
import { getIcon } from "../RegistrationForm";
import useGetFormEdit from "../../hooks/useGetFormEdit";
import CategorySelectorAccordions from "../widgets/CategorySelectorAccordions";
import { LoadingButton } from "@mui/lab";
import TermsConditions from "../templates/TermsConditions";
import { Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";
import { MuiTelInput } from "mui-tel-input";
import SocialMediaInput from "../../widgets/SocialMediaInputs";

// Move FieldRenderer outside to prevent recreation on re-renders
const FieldRenderer = React.memo(({ field, updateFieldProperties }) => {
  const { type, properties } = field;
  const handleChange = useCallback(
    (newV) => {
      updateFieldProperties(field.id, { value: newV });
    },
    [field.id, updateFieldProperties]
  );

  switch (type) {
    case "TextField":
      if (properties.label !== "Mobile No") {
        return (
          <TextField
            type={properties.type}
            label={properties.label}
            required={properties.required}
            multiline={properties.multiline}
            rows={properties.rows}
            value={properties.value || ""}
            onChange={(event) => handleChange(event.target.value)}
            InputProps={
              properties.iconed && properties.iconType
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        {getIcon(properties.iconType, {
                          fontSize: "small",
                          color: "action",
                        })}
                      </InputAdornment>
                    ),
                  }
                : null
            }
            fullWidth
            variant="outlined"
            size="small"
          />
        );
      } else {
        return (
          <MuiTelInput
            size="small"
            defaultCountry="IN"
            onChange={handleChange}
            label={properties.label}
            required={properties.required}
            value={properties.value || ""}
          />
        );
      }
    case "PlainText":
      return (
        <Typography variant={properties.variant}>{properties.label}</Typography>
      );
    case "Checkbox":
      return (
        <FormControlLabel
          control={<Checkbox checked={properties.value || false} />}
          label={properties.label}
          required={properties.required}
          onChange={(event) => handleChange(event.target.checked)}
        />
      );
    case "Divider":
      return <Divider />;
    case "ParagraphDescription":
      return <ParagraphDescription properties={field.properties} />;
    case "RadioGroup":
      return (
        <FormControl component="fieldset">
          <Typography variant="subtitle1">{properties.label}</Typography>
          <RadioGroup row={properties.row}>
            {properties.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio disabled />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    case "Select":
      return (
        <SelectDropDown
          value={properties.value || ""}
          onChange={handleChange}
          properties={properties}
        />
      );
    case "ProductCategories":
      return (
        <CategorySelectorAccordions
          title={`${properties.label}${properties.required ? "*" : ""}`}
          onSelectionChange={handleChange}
        />
      );
    case "DatePicker":
      return (
        <MyDatePicker
          min={properties.minDate}
          max={properties.maxDate}
          label={properties.label}
          value={properties.value || null}
          onChange={handleChange} // Assuming MyDatePicker supports onChange and value props
        />
      );
    case "CountryStateCity":
      const defaultCountry = { name: "India", code: "IN" };
      return (
        <CountryStateCity
          key={properties.value?.country?.code || defaultCountry.code}
          onChange={handleChange}
          initialCountry={properties.value?.country || defaultCountry}
          countryRequired
          stateRequired
        />
      );
    case "TimePicker":
      return (
        <MyTimePicker
          label={properties.label}
          value={properties.value || null}
          onChange={handleChange} // Assuming supports onChange
        />
      );
    case "DateRange":
      return (
        <MyDateRange
          label={properties.label}
          value={properties.value || []}
          onChange={handleChange} // Assuming supports onChange
        />
      );
    case "Switch":
      return (
        <FormControlLabel
          control={
            <Switch
              checked={properties.value || false}
              onChange={(event) => handleChange(event.target.checked)}
            />
          }
          label={properties.labelText || "Active"}
        />
      );
    default:
      return <Typography variant="body2">{field.type}</Typography>;
  }
});

const RegFormContainer = () => {
  const id = APP_ID;
  const theme = useTheme();
  const navigate = useNavigate();

  const url = window.location.href; // Dynamically get the current URL
  const path = new URL(url).pathname; // Get the path, e.g., /visitor-registration/moldex
  const pathSegments = path.split("/").filter((segment) => segment); // Split and remove empty segments
  const lastWordTemp = pathSegments[pathSegments.length - 1]; // Get the last segment
  const lastWord = lastWordTemp === "moldex-fastnex" ? "moldex" : lastWordTemp;

  const [open, setOpen] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { data, isLoading, isError, error } = useTemplateSettings(id);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const hasSetInitialData = useRef(false);
  const [socialData, setSocialData] = useState({});
  const {
    data: formItems,
    isLoading: isFormLoading,
    isError: isFormError,
    error: formError,
  } = useGetFormEdit(id, true);
  const [formFields, setFormFields] = useState([]);
  const templateSettings = data?.properties || {};

  useEffect(() => {
    if (formItems?.form && !hasSetInitialData.current) {
      setFormFields(formItems.form);
      hasSetInitialData.current = true;
    }
  }, [formItems?.form]);

  const updateFieldProperties = useCallback((fieldId, newProperties) => {
    setFormFields((prevFields) =>
      prevFields.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              properties: { ...field.properties, ...newProperties },
            }
          : field
      )
    );
  }, []);

  const getHeight = () => {
    if (isSmallScreen) return "150px";
    if (isMediumScreen) return "200px";
    if (isLargeScreen) return "300px";
  };
  const getVariant = () => {
    if (isSmallScreen) return "h6";
    if (isMediumScreen) return "h5";
    if (isLargeScreen) return "h4";
  };
  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }
  function toCamelCase(str) {
    return str
      .replace(/[^a-zA-Z0-9\s_-]/g, "") // Remove special characters except spaces, underscores, and hyphens
      .toLowerCase()
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
        index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+|[-_]+/g, ""); // Remove spaces, underscores, and hyphens
  }
  const validateForm = (fields) => {
    const errors = [];
    let isValid = true;

    fields.forEach((field) => {
      const properties = field.properties || {};
      const { label, value, required } = properties; // Destructure from "properties"
      console.log("hoi", label, value, required);
      if (!required) return;
      let isInvalid = false;
      // Check for null or undefined
      if (value === null || value === undefined) {
        isInvalid = true;
      }
      // Check for empty string (after trimming)
      else if (typeof value === "string") {
        isInvalid = value.trim() === "";
      }
      // Check for empty array
      else if (Array.isArray(value)) {
        isInvalid = value.length === 0;
      }
      // Check for empty object
      else if (typeof value === "object") {
        isInvalid = Object.keys(value).length === 0;
      }

      if (
        field.label === "Country/State/City" &&
        (value?.state === null || value?.city === null)
      ) {
        isInvalid = true;
      }

      if (isInvalid) {
        isValid = false;
        errors.push(
          `${
            field?.label === "Country/State/City" ? field.label : label
          } is required`
        );
      }
    });

    return { isValid, errors };
  };
  const handleValidationError = (errors) => {
    setValidationErrors(errors);
    setOpenErrorDialog(true);
  };

  const computeSubmitData = () => {
    const data = formFields.reduce((acc, f) => {
      const props = f.properties;
      const val = props.value;
      if (f.type === "CountryStateCity") {
        acc.country = val.country;
        acc.state = val.state;
        acc.city = val.city;
      }

      if (val !== undefined && props.label) {
        const id = toCamelCase(props.label);
        acc[id] = val;
      }
      return acc;
    }, {});

    console.log("csd", data);
    return { ...data, ...socialData };
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        minHeight: "100vh",
        width: "100vw",
        overflow: "auto",
        position: "fixed",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: getHeight(),
          top: 0,
          position: "absolute",
          backgroundImage: `url(${templateSettings.haderBg})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      ></Box>
      <Box
        sx={{
          width: "100%",
          position: "absolute",
        }}
      >
        <Container maxWidth="md">
          <img
            src={
              lastWord === "moldex"
                ? data?.moldexHeader ?? ""
                : templateSettings.headerBanner
            }
            alt="Main Header"
            style={{
              marginTop: "18px",
              width: "100%",
              borderRadius: "4px",
              height: "auto",
              marginBottom: "20px",
            }}
          />
          <BannerSlider
            ADS={
              lastWord === "moldex"
                ? data?.moldexSliders ?? []
                : templateSettings?.headerSliders ?? []
            }
          />
          <Typography
            variant={getVariant()}
            component="h2"
            align="center"
            fontWeight={600}
            my={3}
            sx={{ color: "#E60008" }}
          >
            VISITOR REGISTRATION
          </Typography>
          <Box>
            <Grid container spacing={2}>
              {formFields.map((field) => {
                const gridSize = field?.properties?.grid
                  ? parseInt(field.properties.grid, 10)
                  : 12;

                // On small screens, ensure max 2 columns (min 6 units)
                const responsiveGridSize = isSmallScreen
                  ? Math.max(6, gridSize)
                  : gridSize;

                return (
                  <Grid item xs={responsiveGridSize} key={field.id}>
                    <FieldRenderer
                      field={field}
                      updateFieldProperties={updateFieldProperties}
                    />
                  </Grid>
                );
              })}
            </Grid>
            <SocialMediaInput
              onChange={(obj) => {
                setSocialData((prev) => ({
                  ...prev,
                  ...obj,
                }));
              }}
              values={socialData}
            />
            <Box display={"flex"} justifyContent={"center"}>
              <LoadingButton
                loading={registering}
                onClick={() => {
                  const { isValid, errors } = validateForm(formFields);
                  if (!isValid) {
                    handleValidationError(errors);
                    return;
                  }
                  setOpen(true);
                }}
                sx={{ width: "300px", my: 2 }}
                variant="outlined"
              >
                Submit
              </LoadingButton>
            </Box>
            <Dialog
              open={open}
              onClose={() => setOpen(false)}
              slotProps={{
                paper: {
                  sx: {
                    position: "relative", // Needed for absolute positioning of the close button
                    overflow: "visible", // Allows the button to stay visible when positioned outside
                  },
                },
              }}
            >
              {/* Close button positioned outside the dialog paper */}
              <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                sx={{
                  position: "absolute",
                  right: 0,
                  zIndex: 1,
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  boxShadow: 1,
                  width: 32,
                  height: 32,
                }}
              >
                <Close />
              </IconButton>

              <DialogContent>
                <TermsConditions
                  onContinue={async () => {
                    setOpen(false);
                    try {
                      setRegistering(true);
                      const submitData = computeSubmitData();

                      const response = await axios.post(
                        `${API_ENDPOINT}event/visitor-register`,
                        { ...submitData, eventVisitor: lastWord },
                        getDbHeader(APP_ID)
                      );
                      toast.success(response.data.message);
                      let reg_id = response.data.reg_id;
                      saveReg(reg_id);
                      navigate(`/registration-confirmation`);
                    } catch (error) {
                      toastError(error);
                    } finally {
                      setRegistering(false);
                    }
                  }}
                />
              </DialogContent>
            </Dialog>

            <Dialog
              open={openErrorDialog}
              onClose={() => setOpenErrorDialog(false)}
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  minWidth: "400px",
                },
              }}
            >
              <DialogTitle
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <ErrorOutlineIcon color="error" fontSize="large" />
                <Typography variant="h6" component="div">
                  Please check the following
                </Typography>
              </DialogTitle>

              <DialogContent dividers>
                <List dense sx={{ py: 0 }}>
                  {validationErrors
                    .filter((item) => item !== "undefined is required")
                    .map((error, index) => (
                      <ListItem key={index} sx={{ alignItems: "flex-start" }}>
                        <ListItemIcon sx={{ minWidth: "32px", pt: "4px" }}>
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={error}
                          primaryTypographyProps={{
                            variant: "body1",
                            color: "text.primary",
                          }}
                        />
                      </ListItem>
                    ))}
                </List>
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenErrorDialog(false)}
                  sx={{
                    borderRadius: "8px",
                    px: 3,
                    textTransform: "none",
                  }}
                >
                  Got it!
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Container>
        <RegFooter
          data={data}
          lastWord={lastWord}
          templateSettings={templateSettings}
        />
      </Box>
    </Box>
  );
};

export default RegFormContainer;
